// Left side of the schedule editor (R-S6, decision 036): blocks of the palette and the queue of accepted talks not in the grid yet,
// both dragged onto the grid; the queue is searched by title and speaker.

import { useState } from 'react';
import type { RecordObj } from '@confdeck/core-shared';
import { recordTitle, SearchField } from '@confdeck/core-web';
import type { Drag } from './ScheduleGrid.tsx';
import type { ScheduleData } from './schedule-data.ts';
import s from './schedule.module.css';

/** The palette: what the editor places besides talks, with the glyph it shows (kinds of `slot_kind`). */
export const PALETTE: { kind: string; label: string; glyph: string }[] = [
  { kind: 'break', label: 'Перерыв', glyph: '☕' }, { kind: 'lunch', label: 'Обед', glyph: '🍽' }, { kind: 'opening', label: 'Открытие', glyph: '★' },
  { kind: 'closing', label: 'Закрытие', glyph: '⚑' }, { kind: 'networking', label: 'Нетворкинг', glyph: '🤝' }, { kind: 'sponsor', label: 'Партнёрский блок', glyph: '◆' },
  { kind: 'custom', label: 'Свой блок', glyph: '✎' },
];

export function SchedulePalette({ data, setDrag }: { data: ScheduleData; setDrag: (d: Drag | null) => void }) {
  const { meta, client, talksFree, talkT, linked } = data;
  const [q, setQ] = useState('');
  const speakers = (t: RecordObj) => (talkT ? linked(t, talkT.id, 'speakers').map((sid) => recordTitle(client.store.record(sid), meta)).join(', ') : '');
  const needle = q.trim().toLowerCase();
  const list = talksFree.filter((t) => !needle || `${recordTitle(t, meta)} ${speakers(t)}`.toLowerCase().includes(needle));
  return (
    <aside className={s.palette} aria-label="Блоки и доклады">
      <div className={s.paneHead}>Блоки</div>
      <div className={s.blocks}>
        {PALETTE.map((b) => (
          <div key={b.kind} className={s.block} data-kind={b.kind} draggable onDragStart={(e) => { e.dataTransfer.setData('text/plain', b.kind); setDrag({ kind: 'block', block: b.kind }); }} onDragEnd={() => setDrag(null)}>
            <span className={s.glyph}>{b.glyph}</span>{b.label}
          </div>
        ))}
      </div>
      <div className={s.paneHead}>Не в сетке <span>{talksFree.length}</span></div>
      {talksFree.length > 4 && <div className={s.search}><SearchField size="sm" value={q} onChange={setQ} placeholder="Доклад или докладчик" label="Найти доклад" /></div>}
      <div className={s.queue}>
        {list.map((t) => (
          <div key={t.id} className={s.talkCard} draggable onDragStart={(e) => { e.dataTransfer.setData('text/plain', t.id); setDrag({ kind: 'talk', id: t.id }); }} onDragEnd={() => setDrag(null)}>
            <div className={s.talkTitle}>{recordTitle(t, meta)}</div>
            <div className={s.talkMeta}>{Number(t.data.duration ?? 40)} мин{speakers(t) ? ` · ${speakers(t)}` : ''}</div>
          </div>
        ))}
        {!talksFree.length && <div className={s.hint}>Все принятые доклады стоят в сетке.</div>}
        {talksFree.length > 0 && !list.length && <div className={s.hint}>Ничего не нашлось.</div>}
      </div>
    </aside>
  );
}
